'use client';

import { Box, Container, Stack, Typography } from '@mui/material';
import cx from 'clsx';
import { useTranslations } from 'next-intl';

import FeatureCard from '@/components/FeaturesSection/FeatureCard';
import { Feature } from '@/types/feature.type';
import useScrollReveal from '@/utils/hooks/useScrollReveal';

import styles from './FeaturesSection.module.scss';

interface FeaturesSectionProps {
  currentSlug?: string;
  title?: string;
}

const FeaturesSection = ({ currentSlug, title }: FeaturesSectionProps) => {
  const t = useTranslations('common.featuresSection');

  const featuresArray: Array<Feature & { slug: string }> = [
    {
      slug: 'discovery',
      title: t('discovery.title'),
      description: t('discovery.description'),
      image: { src: '/images/services/discovery.webp', alt: 'Discovery' },
      listItems: t.raw('discovery.listItems') as string[],
      techStack: ['FigJam', 'Claude', 'Jira'],
      bulletColor: 'blue',
      link: {
        href: '/services/discovery',
        text: t('discovery.link'),
      },
    },
    {
      slug: 'design',
      title: t('design.title'),
      description: t('design.description'),
      image: { src: '/images/services/design.webp', alt: 'Design' },
      listItems: t.raw('design.listItems') as string[],
      techStack: ['Figma', 'Photoshop', 'Illustrator', 'After Effects'],
      bulletColor: 'pink',
      link: {
        href: '/services/design',
        text: t('design.link'),
      },
    },
    {
      slug: 'development',
      title: t('development.title'),
      description: t('development.description'),
      image: { src: '/images/services/development.webp', alt: 'Development' },
      listItems: t.raw('development.listItems') as string[],
      techStack: ['Next.js', 'React', 'Java', 'Webflow', 'WordPress'],
      bulletColor: 'green',
      link: {
        href: '/services/development',
        text: t('development.link'),
      },
    },
    {
      slug: 'delivery',
      title: t('delivery.title'),
      description: t('delivery.description'),
      image: { src: '/images/services/delivery.webp', alt: 'Delivery' },
      listItems: t.raw('delivery.listItems') as string[],
      techStack: ['AWS', 'Vercel'],
      bulletColor: 'orange',
      link: {
        href: '/services/delivery',
        text: t('delivery.link'),
      },
    },
  ];

  const { setRef, isVisible } = useScrollReveal<HTMLDivElement>(featuresArray.length);
  const filteredFeatures = currentSlug ? featuresArray.filter(feature => feature.slug !== currentSlug) : featuresArray;

  return (
    <Container component="section" maxWidth="xl" classes={{ root: styles.root }} className={styles.container}>
      {title && (
        <Typography variant="h2" textAlign="center" mb={{ xs: 8, md: 18.5 }} maxWidth={684} marginInline="auto">
          {title}
        </Typography>
      )}
      <Stack className={styles.wrapper}>
        {filteredFeatures.map((feature, index) => (
          <Box
            key={feature.title}
            ref={setRef(index)}
            className={cx(styles.featureWrapper, { [styles.visible]: isVisible(index) })}
          >
            <FeatureCard reverse={index % 2 !== 0} {...feature} />
          </Box>
        ))}
      </Stack>
    </Container>
  );
};

export default FeaturesSection;
